import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/constants';
import ChurchAdminsService from '@/services/church-admins.service';
import type { PagedListRequest } from '@/types';

type UseChurchAdminCurrentScopesOptions = {
  filters?: PagedListRequest;
  enabled?: boolean;
};

export const useChurchAdminCurrentScopes = (options?: UseChurchAdminCurrentScopesOptions) => {
  const { filters, enabled = true } = options || {};

  return useQuery({
    queryKey: [QUERY_KEYS.CHURCH_ADMIN_CURRENT_SCOPES, filters],
    queryFn: () => ChurchAdminsService.getCurrentScopes(filters),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useChurchAdminCurrentScopesInfinite = (options?: UseChurchAdminCurrentScopesOptions) => {
  const { filters, enabled = true } = options || {};

  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.CHURCH_ADMIN_CURRENT_SCOPES, 'infinite', filters],
    queryFn: ({ pageParam = 1 }) =>
      ChurchAdminsService.getCurrentScopes({
        ...filters,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, total, limit } = lastPage;
      const totalPages = Math.ceil(total / limit);
      return page < totalPages ? page + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
      scopes: data.pages.flatMap((page) => page.scopes),
      total: data.pages[0]?.total || 0,
    }),
    enabled,
  });
};
